// CR036: Page List View Component
// CR431: Render page images from storage paths via batch URL signing

import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CheckCircle2, XCircle, SkipForward, Loader2 } from 'lucide-react';
import { useSignedStorageUrls } from '@/hooks/useSignedStorageUrls';

interface PageData {
  id: string;
  page_number: number;
  image_url: string;
  image_storage_path?: string | null;
  thumbnail_url?: string;
  thumbnail_storage_path?: string | null;
  primary_ocr_score_total: number;
  primary_ocr_engine: string;
  candidate_for_ai: boolean;
  candidate_score: number;
  candidate_threshold: number;
  ai_status: string;
  deals_count: number;
}

interface PageListViewProps {
  pages: PageData[];
  onSelectPage: (pageNumber: number) => void;
}

export function PageListView({ pages, onSelectPage }: PageListViewProps) {
  // CR431: Collect all storage paths for batch resolution
  const storagePaths = pages.flatMap((p) => [
    p.thumbnail_storage_path,
    p.image_storage_path,
  ]);
  const { resolveUrl } = useSignedStorageUrls(storagePaths);

  const getAiStatusIcon = (status: string, isCandidate: boolean) => {
    if (!isCandidate) {
      return <SkipForward className="h-4 w-4 text-muted-foreground" />;
    }
    switch (status) {
      case 'complete':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'pending':
        return <Loader2 className="h-4 w-4 text-blue-500" />;
      default:
        return <SkipForward className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getCandidateBadge = (page: PageData) => {
    if (page.candidate_for_ai) {
      return (
        <Badge variant="default" className="text-xs bg-green-100 text-green-800">
          Candidate ({page.candidate_score.toFixed(1)}/{page.candidate_threshold})
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="text-xs">
        Not Candidate ({page.candidate_score.toFixed(1)}/{page.candidate_threshold})
      </Badge>
    );
  };

  /** CR431: Resolve the best available URL for a page thumbnail */
  const resolvePageThumbUrl = (page: PageData): string | null => {
    // Priority: thumbnail_storage_path > thumbnail_url > image_storage_path > image_url
    return resolveUrl(page.thumbnail_storage_path)
      ?? page.thumbnail_url
      ?? resolveUrl(page.image_storage_path)
      ?? page.image_url
      ?? null;
  };

  return (
    <ScrollArea className="h-[300px]">
      <div className="p-2 space-y-1">
        {pages.map((page) => {
          const thumbSrc = resolvePageThumbUrl(page);
          return (
            <div
              key={page.id}
              className="flex items-center gap-3 p-2 rounded-md hover:bg-muted cursor-pointer transition-colors"
              onClick={() => onSelectPage(page.page_number)}
            >
              {/* Thumbnail */}
              <div className="w-12 h-16 bg-muted rounded border overflow-hidden flex-shrink-0">
                {thumbSrc ? (
                  <img
                    src={thumbSrc}
                    alt={`Page ${page.page_number}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
                    P{page.page_number}
                  </div>
                )}
              </div>

              {/* Page Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">Page {page.page_number}</span>
                  {getAiStatusIcon(page.ai_status, page.candidate_for_ai)}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="text-xs">
                    OCR: {page.primary_ocr_score_total.toFixed(0)}
                  </Badge>
                  {getCandidateBadge(page)}
                </div>
              </div>

              {/* Deal Count */}
              {page.deals_count > 0 && (
                <Badge className="text-xs">
                  {page.deals_count} deal{page.deals_count > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          );
        })}
        {pages.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">
            No pages processed yet.
          </p>
        )}
      </div>
    </ScrollArea>
  );
}